import { payorSchema, statusSchema, tariffSchema } from '@billing/validation';
import { DEFAULT_CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import type { z } from 'zod';

export enum PayorType {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
  DIRECT = 'DIRECT',
}

type Status = z.infer<typeof statusSchema>;
type Payor = z.infer<typeof payorSchema>;
type Tariff = z.infer<typeof tariffSchema>;

// TODO(claim-status-migration): remove this and use validation valutions once claim status migration is complete
export const claimStatusValues = {
  paid: 'Paid',
  incomplete: 'Incomplete',
  ready: 'Ready',
  submitted: 'Submitted',
  pending: 'Pending',
  error: 'Error',
  pendingWriteOff: 'Pending Write-Off',
  cancelled: 'Cancelled',
  queried: 'Queried',
  reverted: 'Reverted',
  uninsured: 'Uninsured',
  payorInvoiced: 'Payor Invoiced',
  unmatched: 'Unmatched',
} as const;

const makeStatusSeed = (status: Status) => statusSchema.parse(status);

const makePayorSeed = (
  payor: Pick<Payor, 'id' | 'name' | 'type'> & Partial<Payor>
): Payor => ({
  ...payorSchema.parse({
    id: payor.id,
    name: payor.name,
    type: payor.type,
  }),
  ...payor,
});

const makeTariffSeed = (
  tariff: Pick<
    Tariff,
    | 'id'
    | 'code'
    | 'prefix'
    | 'description'
    | 'effectiveDate'
    | 'specialtyCode'
    | 'type'
    | 'rate'
  > &
    Partial<Tariff>
): Tariff =>
  tariffSchema.parse({
    ...tariff,
    units: 1,
    icd9: '0010B',
    percentage: 100,
    split: tariff.split ?? ' ',
    incision: null,
    total: null,
    isManualTotal: false,
    serviceStart: null,
    serviceEnd: null,
    province: tariff.province ?? 'MB',
    specialtyCode: tariff.specialtyCode ?? 'ALL',
    status: tariff.status ?? DEFAULT_CLAIM_TARIFF_STATUS,
  });

export const statusRefs = {
  paid: makeStatusSeed({
    id: '0a450388-f3c4-4cb4-9b05-2df09467126f',
    type: claimStatusValues.paid,
    description: 'Claim has been paid by health authority',
  }),
  incomplete: makeStatusSeed({
    id: 'bb0e95e4-d63d-4ee8-addf-6d35d4b8fd6e',
    type: claimStatusValues.incomplete,
    description:
      'Not all required data is present on claim for it to be submitted',
  }),
  ready: makeStatusSeed({
    id: '6bcdb829-4b7b-49d3-b4e2-54f20d7a0bc5',
    type: claimStatusValues.ready,
    description:
      'All required data is present and claim is ready to be included in a submission',
  }),
  submitted: makeStatusSeed({
    id: 'e11dc867-11e7-4196-bbc5-fcdbfac6c1fd',
    type: claimStatusValues.submitted,
    description:
      'The claim has been included as part of a submission and has been sent for assessment',
  }),
  pending: makeStatusSeed({
    id: 'c62aa75d-c5c1-4968-ab28-8a6c159d6e33',
    type: claimStatusValues.pending,
    description:
      'The health authority are still in the process of assessing this claim',
  }),
  error: makeStatusSeed({
    id: '0266da80-53b0-473e-90b9-dcaf2f8f8f5a',
    type: claimStatusValues.error,
    description:
      'The health authority is unable to process this claim due to errors in the data that must be addressed before resubmission',
  }),
  pendingWriteOff: makeStatusSeed({
    id: '95213531-66e5-44d8-b40b-e7da56f8b2c8',
    type: claimStatusValues.pendingWriteOff,
    description:
      'The health authority has deemed this claim as not able to be billed',
  }),
  cancelled: makeStatusSeed({
    id: '10b4f17b-0977-4648-a1e4-dd5cd32f6478',
    type: claimStatusValues.cancelled,
    description: 'The claim has been cancelled by a billing clerk',
  }),
  queried: makeStatusSeed({
    id: '0d0f5c87-68e3-4c56-9e03-98f5c2c59212',
    type: claimStatusValues.queried,
    description: 'Claim has been queried ',
  }),
  reverted: makeStatusSeed({
    id: '2244cfdd-8b86-4190-8842-9b4c241ef68e',
    type: claimStatusValues.reverted,
    description: 'A Submission was manually undone ',
  }),
  uninsured: makeStatusSeed({
    id: 'f91cad21-40b4-4baa-b7fa-a294a6b62ad7',
    type: claimStatusValues.uninsured,
    description:
      'Patient is uninsured and requires manual invoicing by clinic staff',
  }),
  payorInvoiced: makeStatusSeed({
    id: 'c54bf714-1594-4b15-9c2a-ef2442d1bd1d',
    type: claimStatusValues.payorInvoiced,
    description: 'Claim Invoice was generated',
  }),
  unmatched: makeStatusSeed({
    id: 'ac1d6e13-e2b2-4ac8-8331-fb3fd37db017',
    type: claimStatusValues.unmatched,
    description:
      'Claim was paid but payor paid for a tariff different than the one submitted and requires manual intervention to apply the new tariff to the claim',
  }),
} satisfies Record<string, Status>;

export const payorRefs = {
  bc: makePayorSeed({
    id: '7e4183a9-b652-4f39-8f5b-8e449f0d4f19',
    name: 'BC',
    province: 'BC',
    type: PayorType.PUBLIC,
  }),
  mb: makePayorSeed({
    id: '7529071b-1c83-4e10-ba1b-9d3cb6a17de2',
    name: 'MB',
    province: 'MB',
    type: PayorType.PUBLIC,
    fax: '2045555555',
  }),
  msh: makePayorSeed({
    id: '05fa0b82-267c-4dff-a97d-80a7a9479959',
    name: 'MSH',
    province: 'MB',

    type: PayorType.PUBLIC,
    fax: '2045555555',
  }),
  qdoc: makePayorSeed({
    id: 'a3f329b1-52d1-4649-83de-9b35a7ef3132',
    name: 'QDOC',
    province: 'MB',

    type: PayorType.PUBLIC,
    fax: '2045555555',
  }),
  wcb: makePayorSeed({
    id: 'eb200898-38be-42de-8c2d-9b3e980bd614',
    name: 'WCB',
    province: 'MB',
    type: PayorType.PRIVATE,
    fax: '2045555555',
  }),
  patient: makePayorSeed({
    id: 'd1cbb3e5-8b9c-4a7c-9a0b-2f1e5f6a7c8d',
    name: 'PATIENT',
    type: PayorType.DIRECT,
  }),
  private: makePayorSeed({
    id: 'e2cbb3e5-8b9c-4a7c-9a0b-2f1e5f6a7c8d',
    name: 'PRIVATE',
    type: PayorType.DIRECT,
  }),
} satisfies Record<string, Payor>;

export const tariffRefs = {
  testing8442: makeTariffSeed({
    id: '31917961-c3b8-4064-bf81-578b380d9667',
    code: '8442',
    description: 'testing tariff that gives docs monies',
    effectiveDate: '2024-04-05',
    type: 'Base',
    rate: '420.69',
    prefix: 8,
    specialtyCode: 'ALL',
  }),
  alcohol9006: makeTariffSeed({
    id: 'b88e6dad-0e62-4ed8-b90e-2af5ae7dfd0b',
    code: '9006',
    description: 'BIOCHEMISTRY, ALCOHOL',
    effectiveDate: '1999-04-01',
    type: 'Base',
    rate: '23.70',
    specialtyCode: 'ALL',
    prefix: 8,
  }),
  virtual8321: makeTariffSeed({
    id: 'bf498a59-c72c-4628-bca8-724962362e32',
    code: '8321',
    description: 'VIRTUAL VISIT BY TELEPHONE OR VIDEO',
    effectiveDate: '2024-04-01',
    type: 'Base',
    rate: '58.43',
    specialtyCode: '029',
    prefix: 7,
  }),
  modifier2789: makeTariffSeed({
    id: '16c65dc3-e2c5-406e-b621-0b5dd91fff1f',
    code: '2789',
    description: 'TONGUE, UNLISTED OR UNUSUALLY COMPLICATED',
    effectiveDate: '2024-04-01',
    type: 'Modifier',
    rate: '0.00',
    specialtyCode: 'ALL',
    prefix: 2,
  }),
  virtual8447: makeTariffSeed({
    id: 'd3fd92dd-8baf-4379-b1e4-b358919dc5c6',
    code: '8447',
    description: 'COMPREHENSIVE VIRTUAL ASSESSMENT BY SPECIALIST',
    effectiveDate: '2024-04-01',
    type: 'Base',
    rate: '101.53',
    specialtyCode: '02 ',
    prefix: 7,
  }),
  premium5550: makeTariffSeed({
    id: '77761664-5ea1-437a-bc06-50a716c3c143',
    code: '5550',
    description:
      'AFTER HOURS PREMIUM - 0700 TO 0000 HRS (7 AM TO MIDNIGHT) ON SAT, SUN HOLIDAYS, ADD 50%',
    effectiveDate: '2000-02-01',
    type: 'Modifier',
    rate: '0.5',
    specialtyCode: 'ALL',
    prefix: 7,
  }),
} satisfies Record<string, Tariff>;

export const canonicalStatuses: Status[] = [
  statusRefs.paid,
  statusRefs.incomplete,
  statusRefs.ready,
  statusRefs.submitted,
  statusRefs.pending,
  statusRefs.error,
  statusRefs.pendingWriteOff,
  statusRefs.cancelled,
  statusRefs.queried,
  statusRefs.reverted,
  statusRefs.uninsured,
  statusRefs.payorInvoiced,
  statusRefs.unmatched,
];

export const canonicalPayors: Payor[] = [
  payorRefs.mb,
  payorRefs.bc,
  payorRefs.msh,
  payorRefs.qdoc,
  payorRefs.wcb,
  payorRefs.patient,
  payorRefs.private,
];

export const canonicalTariffs: Tariff[] = [
  tariffRefs.testing8442,
  tariffRefs.alcohol9006,
  tariffRefs.virtual8321,
  tariffRefs.modifier2789,
  tariffRefs.virtual8447,
  tariffRefs.premium5550,
];

export const canonicalStatusByID = new Map(
  canonicalStatuses.map((status) => [status.id, status])
);
export const canonicalPayorByID = new Map(
  canonicalPayors.map((payor) => [payor.id, payor])
);
export const canonicalTariffByID = new Map(
  canonicalTariffs.map((tariff) => [tariff.id, tariff])
);
